// Page de test temporaire de l'API finances-api-pes (outil de dev, à supprimer).
// Tout est volontairement regroupé dans ce seul fichier.
import type {
	Bordereau,
	Exercice,
	ParametreGeneralEntite,
} from '@mairistem/comptabilite';
import { Combobox } from '@mairistem/finances-utils';
import { request } from '@mairistem/tools';
import React, { useEffect, useRef, useState } from 'react';
import CODE_BHM from '../../constants/mouvements/codeBHM';
import { getExercices } from '../../services/entites/exercices';
import { getBordereaux } from '../../services/piecesComptables/bordereaux/bordereaux';
import { getPgEntites } from '../../utils/marcheMarco/marcheMarco';

type FieldType =
	| 'number'
	| 'text'
	| 'bool'
	| 'ints'
	| 'date'
	| 'sens'
	| 'file64'
	| 'file';

interface Field {
	name: string;
	type: FieldType;
	required?: boolean;
	path?: boolean;
	defaultValue?: boolean;
	maxLength?: number;
	hint?: string;
}

interface Route {
	key: string;
	method: 'GET' | 'POST';
	path: string;
	fields: Field[];
	generation?: boolean;
	surcharges?: boolean;
	note?: string;
}

type Value = string | boolean | File | null;

interface ResultState {
	status: number;
	contentType: string;
	pesHeaders: Record<string, string>;
	json?: unknown;
	text?: string;
	fichier?: string;
}

interface HistoryEntry {
	id: number;
	at: string;
	route: string;
	status: number | string;
	duree: number;
}

// La passerelle retire /api/finances/pes ; les routes de l'API commencent par /pes.
const BASE_URL = '/api/finances/pes/pes';

const PES_HEADERS = [
	'X-Pes-Conforme',
	'X-Pes-Anomalies',
	'X-Pes-Avertissements',
	'X-Pes-Fichier',
	'X-Debug',
];

const CONTEXT_HEADERS: {
	name: string;
	storageKey: string;
}[] = [{ name: 'X-Entite', storageKey: 'identifiantEntite' }];

const STORAGE_KEY = 'testPes';

interface Stored {
	context: Record<string, string>;
	routeKey: string;
	values: Record<string, Value>;
	sent: string;
	result: ResultState | null;
}

interface PesResponse {
	status: number;
	headers: Record<string, string | undefined>;
	data: Blob;
}

const loadStored = (): Partial<Stored> => {
	try {
		return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
	} catch {
		return {};
	}
};

const defaultContext = () =>
	Object.fromEntries(
		CONTEXT_HEADERS.map(({ name, storageKey }) => [
			name,
			sessionStorage.getItem(storageKey) ?? '',
		]),
	);

const DEFAULT_VALUES: Record<string, Value> = { controler: true };

const GENERATION_FIELDS: Field[] = [
	{ name: 'enregistrer', type: 'bool' },
	{ name: 'controler', type: 'bool', defaultValue: true },
	{ name: 'asynchrone', type: 'bool' },
];

const SURCHARGE_FIELDS: Field[] = [
	{ name: 'sigleRecepteur', type: 'text' },
	{ name: 'codCol', type: 'text', maxLength: 3 },
	{ name: 'codBud', type: 'text', maxLength: 2 },
	{ name: 'idColl', type: 'text', maxLength: 14 },
	{ name: 'libelleColBud', type: 'text' },
];

const ROUTES: Route[] = [
	{
		key: 'bordereaux',
		method: 'POST',
		path: '/aller/bordereaux',
		generation: true,
		surcharges: true,
		fields: [
			{ name: 'identifiantExercice', type: 'number', required: true },
			{ name: 'sens', type: 'sens', required: true },
			{ name: 'bordereaux', type: 'ints' },
			{ name: 'infoDematerialise', type: 'bool' },
			{ name: 'mode360', type: 'bool' },
			{ name: 'pjAExporter', type: 'ints' },
			{ name: 'pjUniquement', type: 'bool' },
		],
	},
	{
		key: 'bordereau',
		method: 'POST',
		path: '/aller/bordereaux/{identifiantBordereau}',
		generation: true,
		surcharges: true,
		note: "Passe en asynchrone au-delà d'un seuil de pièces.",
		fields: [
			{
				name: 'identifiantBordereau',
				type: 'number',
				required: true,
				path: true,
			},
			{ name: 'infoDematerialise', type: 'bool' },
			{ name: 'mode360', type: 'bool' },
			{ name: 'pjAExporter', type: 'ints' },
			{ name: 'pjUniquement', type: 'bool' },
		],
	},
	{
		key: 'factures',
		method: 'POST',
		path: '/aller/factures/{identifiantBordereau}',
		generation: true,
		surcharges: true,
		fields: [
			{
				name: 'identifiantBordereau',
				type: 'number',
				required: true,
				path: true,
			},
			{ name: 'nomFichier', type: 'text' },
		],
	},
	{
		key: 'marches',
		method: 'POST',
		path: '/aller/marches/{identifiantMarche}',
		generation: true,
		surcharges: true,
		fields: [
			{ name: 'identifiantMarche', type: 'number', required: true, path: true },
			{ name: 'identifiantExercice', type: 'number', required: true },
			{ name: 'caractereCorrectif', type: 'bool' },
			{ name: 'pjUniquement', type: 'bool' },
		],
	},
	{
		key: 'pjs',
		method: 'POST',
		path: '/aller/pjs',
		generation: true,
		surcharges: true,
		fields: [
			{ name: 'identifiantExercice', type: 'number', required: true },
			{ name: 'idPj', type: 'ints', required: true },
			{
				name: 'refCompta',
				type: 'text',
				required: true,
				hint: 'Domaine-Exercice-Id[,Id]-TypeObjet',
			},
			{ name: 'pjAExporter', type: 'ints' },
		],
	},
	{
		key: 'budget',
		method: 'POST',
		path: '/aller/budget',
		generation: true,
		note: 'identifiantMaquette OU documentBase64.',
		fields: [
			{ name: 'identifiantExercice', type: 'number', required: true },
			{ name: 'identifiantMaquette', type: 'number' },
			{ name: 'documentBase64', type: 'file64' },
			{ name: 'nomDocument', type: 'text' },
			{ name: 'cfu', type: 'bool' },
			{ name: 'pdfBase64', type: 'file64' },
			{ name: 'datePref', type: 'date' },
		],
	},
	{
		key: 'rolmre',
		method: 'POST',
		path: '/aller/rolmre',
		generation: true,
		note: 'Envoyé en multipart/form-data dès qu’un fichier est choisi.',
		fields: [
			{ name: 'fichier', type: 'file' },
			{ name: 'archivePj', type: 'file' },
			{ name: 'gamme', type: 'text' },
			{ name: 'regroupement', type: 'text' },
			{ name: 'articleDefaut', type: 'text' },
			{ name: 'fonctionDefaut', type: 'text' },
			{ name: 'serviceDefaut', type: 'text' },
			{ name: 'refTiersDefaut', type: 'text' },
			{ name: 'nomTiersGlobal', type: 'text' },
			{ name: 'catTiersDefaut', type: 'text' },
			{ name: 'natJurDefaut', type: 'text' },
			{ name: 'idPceInitial', type: 'number' },
			{ name: 'idBord', type: 'number' },
			{ name: 'idPost', type: 'text' },
			{ name: 'idColl', type: 'text', maxLength: 14 },
			{ name: 'codCol', type: 'text', maxLength: 3 },
			{ name: 'codBud', type: 'text', maxLength: 2 },
			{ name: 'libelleColBud', type: 'text' },
			{ name: 'finJur', type: 'text' },
			{ name: 'infoDematerialise', type: 'bool' },
		],
	},
	{
		key: 'valider',
		method: 'POST',
		path: '/aller/valider',
		note: 'Fichier XML encodé en base64 brut (ISO-8859-1, pas de réencodage).',
		fields: [{ name: 'xmlBase64', type: 'file64', required: true }],
	},
	{
		key: 'traitement',
		method: 'GET',
		path: '/traitements/{id}',
		fields: [{ name: 'id', type: 'text', required: true, path: true }],
	},
	{
		key: 'traitementFichier',
		method: 'GET',
		path: '/traitements/{id}/fichier',
		fields: [{ name: 'id', type: 'text', required: true, path: true }],
	},
	{
		key: 'fichier',
		method: 'GET',
		path: '/fichiers/{id}',
		fields: [{ name: 'id', type: 'text', required: true, path: true }],
	},
	{
		key: 'fichierXml',
		method: 'GET',
		path: '/fichiers/{id}/xml',
		fields: [{ name: 'id', type: 'text', required: true, path: true }],
	},
];

const readBase64 = (file: File) =>
	new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = String(reader.result);
			resolve(result.slice(result.indexOf(',') + 1));
		};
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(file);
	});

const parseFilename = (disposition: string | null, fallback: string) => {
	const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
	return match ? decodeURIComponent(match[1]) : fallback;
};

const downloadBlob = (blob: Blob, filename: string) => {
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(url);
};

// Les chaînes base64 sont tronquées à l'affichage uniquement.
const preview = (body: unknown) =>
	JSON.stringify(
		body,
		(_, v) =>
			typeof v === 'string' && v.length > 200
				? `${v.slice(0, 60)}… (${v.length} car.)`
				: v instanceof File
					? `[Fichier ${v.name}, ${v.size} o]`
					: v,
		2,
	);

const routeFields = (route: Route) => [
	...route.fields,
	...(route.generation ? GENERATION_FIELDS : []),
];

const TestPes = () => {
	const [saved] = useState(loadStored);
	const [context, setContext] = useState<Record<string, string>>(() => ({
		...defaultContext(),
		...saved.context,
	}));
	const [routeKey, setRouteKey] = useState(saved.routeKey ?? ROUTES[0].key);
	const [values, setValues] = useState<Record<string, Value>>(
		saved.values ?? DEFAULT_VALUES,
	);
	const [sent, setSent] = useState<string>(saved.sent ?? '');
	const [result, setResult] = useState<ResultState | null>(
		saved.result ?? null,
	);
	const [error, setError] = useState<string>('');
	const [loading, setLoading] = useState(false);
	const [history, setHistory] = useState<HistoryEntry[]>([]);
	const [suivi, setSuivi] = useState<string>('');
	// Change la clé du formulaire pour vider les inputs file.
	const [resetCount, setResetCount] = useState(0);
	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const [entites, setEntites] = useState<ParametreGeneralEntite[]>([]);
	const [exercices, setExercices] = useState<Exercice[]>([]);
	const [bordereaux, setBordereaux] = useState<Bordereau[]>([]);
	const [historise, setHistorise] = useState(false);

	const identifiantEntite = context['X-Entite']?.trim() ?? '';
	const identifiantExercice = String(values.identifiantExercice ?? '');
	const sens = String(values.sens ?? '');

	useEffect(() => {
		getPgEntites()
			.then(({ data }) => setEntites(data ?? []))
			.catch(() => setError('Impossible de récupérer les entités'));
	}, []);

	useEffect(() => {
		setExercices([]);
		if (identifiantEntite === '') {
			return;
		}
		getExercices(new URLSearchParams({ identifiantEntite }))
			.then(({ data }) => setExercices(data ?? []))
			.catch(() => setError('Impossible de récupérer les exercices'));
	}, [identifiantEntite]);

	useEffect(() => {
		setBordereaux([]);
		if (identifiantEntite === '' || identifiantExercice === '') {
			return;
		}
		getBordereaux(
			new URLSearchParams({
				all: 'true',
				identifiantEntite,
				identifiantExercice,
				...(sens !== '' && { sens }),
				...(historise && { codeBHM: CODE_BHM.VALUE.HISTORISE }),
			}),
		)
			.then(({ data }) => setBordereaux(data ?? []))
			.catch(() => setError('Impossible de récupérer les bordereaux'));
	}, [identifiantEntite, identifiantExercice, sens, historise]);

	const renderExerciceSelect = (id: string) => (
		<select
			id={id}
			value={identifiantExercice}
			onChange={(e) => setValue('identifiantExercice', e.target.value)}
		>
			<option value="">— exercice —</option>
			{exercices.map((exercice) => (
				<option key={exercice.identifiant} value={exercice.identifiant}>
					{exercice.anneeExercice} ({exercice.identifiant})
				</option>
			))}
		</select>
	);

	const route = ROUTES.find(({ key }) => key === routeKey) ?? ROUTES[0];

	const stopPolling = () => {
		if (pollRef.current) {
			clearInterval(pollRef.current);
			pollRef.current = null;
		}
		setSuivi('');
	};

	useEffect(
		() => () => {
			if (pollRef.current) {
				clearInterval(pollRef.current);
			}
		},
		[],
	);

	useEffect(() => {
		// Les fichiers ne sont pas sérialisables : ils ne sont pas conservés.
		const stored: Stored = {
			context,
			routeKey,
			values: Object.fromEntries(
				Object.entries(values).filter(([, v]) => !(v instanceof File)),
			),
			sent,
			result,
		};
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
		} catch {
			// localStorage indisponible : on garde seulement l'état en mémoire.
		}
	}, [context, routeKey, values, sent, result]);

	const clearAll = () => {
		stopPolling();
		try {
			localStorage.removeItem(STORAGE_KEY);
		} catch {
			// localStorage indisponible.
		}
		setContext(defaultContext());
		setRouteKey(ROUTES[0].key);
		setValues(DEFAULT_VALUES);
		setSent('');
		setResult(null);
		setError('');
		setHistory([]);
		setResetCount((n) => n + 1);
	};

	const setValue = (name: string, value: Value) =>
		setValues((prev) => ({ ...prev, [name]: value }));

	const changeRoute = (key: string) => {
		setRouteKey(key);
		// Les inputs file sont remontés vides : on oublie les fichiers choisis.
		setValues((prev) =>
			Object.fromEntries(
				Object.entries(prev).filter(([, v]) => !(v instanceof File)),
			),
		);
	};

	// Content-Type (JSON / multipart) et authentification sont gérés par request.
	const buildHeaders = () => {
		const headers: Record<string, string> = { 'X-Debug': '1' };
		Object.entries(context).forEach(([name, value]) => {
			if (value.trim() !== '') {
				headers[name] = value.trim();
			}
		});
		return headers;
	};

	const call = async (
		method: string,
		path: string,
		label: string,
		body?: Record<string, unknown> | FormData,
	) => {
		const url = `${BASE_URL}${path}`;
		const isForm = body instanceof FormData;
		setSent(
			`${method} ${url}\n${
				body === undefined
					? ''
					: isForm
						? preview(Object.fromEntries(body.entries()))
						: preview(body)
			}`,
		);
		setResult(null);
		setError('');

		const start = performance.now();
		let status: number | string = 'erreur';
		try {
			let response: PesResponse;
			try {
				response = await request.custom<unknown, PesResponse>(
					method.toLowerCase(),
					url,
					body,
					{
						headers: buildHeaders(),
						responseType: 'blob',
						signal: new AbortController().signal,
					},
				);
			} catch (e) {
				// Hors 200/201/204 (202, 4xx, 5xx) : la réponse est dans l'erreur axios.
				const errorResponse = (e as { response?: PesResponse }).response;
				if (!errorResponse) {
					throw e;
				}
				response = errorResponse;
			}
			status = response.status;
			const header = (name: string) =>
				response.headers[name.toLowerCase()] ?? null;
			const contentType = header('Content-Type') ?? '';
			const pesHeaders = Object.fromEntries(
				PES_HEADERS.flatMap((name) => {
					const value = header(name);
					return value === null ? [] : [[name, String(value)]];
				}),
			);
			const state: ResultState = { status, contentType, pesHeaders };

			if (/zip|xml/i.test(contentType)) {
				const fallback = contentType.includes('zip') ? 'pes.zip' : 'pes.xml';
				const filename = parseFilename(header('Content-Disposition'), fallback);
				downloadBlob(response.data, filename);
				state.fichier = filename;
			} else {
				const text = response.data ? await response.data.text() : '';
				try {
					state.json = text === '' ? undefined : JSON.parse(text);
				} catch {
					state.text = text;
				}
			}
			setResult(state);
			return state;
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
			return null;
		} finally {
			const duree = Math.round(performance.now() - start);
			setHistory((prev) =>
				[
					{
						id: Date.now() + Math.random(),
						at: new Date().toLocaleTimeString(),
						route: label,
						status,
						duree,
					},
					...prev,
				].slice(0, 20),
			);
			setLoading(false);
		}
	};

	const send = async () => {
		const fields = [
			...routeFields(route),
			...(route.surcharges ? SURCHARGE_FIELDS : []),
		];
		const missing = fields.filter(({ name, required }) => {
			const v = values[name];
			return required && (v === undefined || v === null || v === '');
		});
		if (missing.length > 0) {
			setError(
				`Champs obligatoires : ${missing.map(({ name }) => name).join(', ')}`,
			);
			return;
		}

		setLoading(true);
		let path = route.path;
		const body: Record<string, unknown> = {};
		const files: Record<string, File> = {};

		for (const field of fields) {
			const value = values[field.name];
			if (field.path) {
				path = path.replace(
					`{${field.name}}`,
					encodeURIComponent(String(value)),
				);
				continue;
			}
			if (field.type === 'bool') {
				// Coché : envoyé. Booléen à défaut true : toujours envoyé.
				if (value === true || field.defaultValue === true) {
					body[field.name] = value === true;
				}
				continue;
			}
			if (value === undefined || value === null || value === '') {
				continue;
			}
			if (field.type === 'file' && value instanceof File) {
				files[field.name] = value;
			} else if (field.type === 'file64' && value instanceof File) {
				body[field.name] = await readBase64(value);
			} else if (field.type === 'number') {
				body[field.name] = Number(value);
			} else if (field.type === 'ints') {
				body[field.name] = String(value)
					.split(',')
					.map((s) => s.trim())
					.filter((s) => s !== '')
					.map(Number);
			} else {
				body[field.name] = value;
			}
		}

		let payload: Record<string, unknown> | FormData | undefined =
			route.method === 'GET' ? undefined : body;
		if (Object.keys(files).length > 0) {
			const form = new FormData();
			Object.entries(body).forEach(([name, v]) => {
				if (Array.isArray(v)) {
					v.forEach((item) => {
						form.append(`${name}[]`, String(item));
					});
				} else {
					form.append(
						name,
						typeof v === 'boolean' ? (v ? '1' : '0') : String(v),
					);
				}
			});
			Object.entries(files).forEach(([name, file]) => {
				form.append(name, file);
			});
			payload = form;
		}

		await call(route.method, path, `${route.method} ${route.path}`, payload);
	};

	const traitementId = (json: unknown) => {
		const data = (json ?? {}) as Record<string, any>;
		return String(
			data.id ?? data.identifiant ?? data.traitement?.id ?? data.data?.id ?? '',
		);
	};

	const suivre = (id: string) => {
		stopPolling();
		setSuivi(id);
		let enCours = false;
		const tick = async () => {
			if (enCours) {
				return;
			}
			enCours = true;
			setLoading(true);
			const state = await call(
				'GET',
				`/traitements/${encodeURIComponent(id)}`,
				`GET /traitements/{id} (suivi)`,
			);
			const data = (state?.json ?? {}) as Record<string, any>;
			const statut = String(
				data.statut ?? data.etat ?? data.status ?? data.data?.statut ?? '',
			).toUpperCase();
			if (
				!state ||
				statut === 'ECHEC' ||
				(state.status >= 400 && state.status !== 404)
			) {
				stopPolling();
			} else if (statut === 'TERMINE') {
				stopPolling();
				setLoading(true);
				await call(
					'GET',
					`/traitements/${encodeURIComponent(id)}/fichier`,
					'GET /traitements/{id}/fichier (auto)',
				);
			}
			enCours = false;
		};
		pollRef.current = setInterval(tick, 2000);
		tick();
	};

	// Champ ints (liste d'identifiants) : sélection multiple.
	const renderBordereauCombobox = (field: Field, id: string) => {
		const multiple = field.type === 'ints';
		const value = String(values[field.name] ?? '');
		const selected = multiple
			? value
					.split(',')
					.map((s) => s.trim())
					.filter((s) => s !== '')
			: value;
		return (
			<span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
				{/* Les routes sans identifiantExercice en ont besoin pour lister. */}
				{!route.fields.some(({ name }) => name === 'identifiantExercice') &&
					renderExerciceSelect(`${id}-exercice`)}
				<Combobox
					id={id}
					style={{ minWidth: 420 }}
					search
					selection
					clearable
					multiple={multiple}
					placeholder="Rechercher un bordereau"
					noResultsMessage="Aucun bordereau"
					options={bordereaux.map((bordereau) => ({
						key: bordereau.identifiant,
						value: String(bordereau.identifiant),
						text: `${bordereau.codeBHM}/${bordereau.numero}(${bordereau.sens}) ${bordereau.libelle} [${bordereau.identifiant}]`,
					}))}
					value={selected}
					onChange={(_e, { value: v }) =>
						setValue(
							field.name,
							Array.isArray(v) ? v.join(',') : String(v ?? ''),
						)
					}
				/>
				<label>
					<input
						type="checkbox"
						checked={historise}
						onChange={(e) => setHistorise(e.target.checked)}
					/>{' '}
					Historisés (H)
				</label>
			</span>
		);
	};

	const renderField = (field: Field) => {
		const value = values[field.name];
		const id = `test-pes-${field.name}`;
		const label = (
			<label htmlFor={id} style={{ display: 'inline-block', width: 180 }}>
				{field.name}
				{field.required ? ' *' : ''}
				{field.path ? ' (chemin)' : ''}
			</label>
		);
		let input: React.ReactNode;
		switch (field.type) {
			case 'bool':
				input = (
					<input
						id={id}
						type="checkbox"
						checked={
							value === undefined ? field.defaultValue === true : value === true
						}
						onChange={(e) => setValue(field.name, e.target.checked)}
					/>
				);
				break;
			case 'sens':
				input = (
					<select
						id={id}
						value={(value as string) ?? ''}
						onChange={(e) => setValue(field.name, e.target.value)}
					>
						<option value="">—</option>
						<option value="D">D (dépense)</option>
						<option value="R">R (recette)</option>
					</select>
				);
				break;
			case 'number':
				if (field.name === 'identifiantExercice' && exercices.length > 0) {
					input = renderExerciceSelect(id);
					break;
				}
				if (field.name === 'identifiantBordereau') {
					input = renderBordereauCombobox(field, id);
					break;
				}
				input = (
					<input
						id={id}
						type="number"
						value={(value as string) ?? ''}
						onChange={(e) => setValue(field.name, e.target.value)}
					/>
				);
				break;
			case 'file':
			case 'file64':
				input = (
					<input
						id={id}
						type="file"
						onChange={(e) => setValue(field.name, e.target.files?.[0] ?? null)}
					/>
				);
				break;
			default:
				if (field.name === 'bordereaux') {
					input = renderBordereauCombobox(field, id);
					break;
				}
				input = (
					<input
						id={id}
						type={field.type === 'ints' ? 'text' : field.type}
						value={(value as string) ?? ''}
						maxLength={field.maxLength}
						placeholder={field.type === 'ints' ? '1,2,3' : field.hint}
						onChange={(e) => setValue(field.name, e.target.value)}
					/>
				);
		}
		return (
			<div key={field.name} style={{ margin: '4px 0' }}>
				{label}
				{input}
			</div>
		);
	};

	const validationErrors =
		result?.status === 422
			? ((result.json as Record<string, any>)?.errors as
					| Record<string, string[]>
					| undefined)
			: undefined;
	const asyncId = result?.status === 202 ? traitementId(result.json) : '';

	return (
		<div
			style={{
				padding: 16,
				fontFamily: 'sans-serif',
				fontSize: 13,
				height: '100%',
				maxHeight: '100vh',
				overflow: 'auto',
				boxSizing: 'border-box',
			}}
		>
			<h2>Test API PES (debug)</h2>

			<fieldset>
				<legend>Contexte</legend>
				<div style={{ margin: '4px 0' }}>
					<label>
						<span style={{ display: 'inline-block', width: 180 }}>
							Entité (X-Entite)
						</span>
						<select
							style={{ width: 420 }}
							value={identifiantEntite}
							onChange={(e) => {
								const { value } = e.target;
								setContext((prev) => ({ ...prev, 'X-Entite': value }));
							}}
						>
							<option value="">—</option>
							{entites.map((pgEntite) => (
								<option
									key={pgEntite.identifiant}
									value={pgEntite.identifiantEntite}
								>
									{pgEntite.entite?.libelle ?? pgEntite.raisonSociale} (
									{pgEntite.identifiantEntite})
								</option>
							))}
						</select>
					</label>
				</div>
				<div style={{ color: 'gray' }}>X-Debug: 1 est toujours envoyé.</div>
			</fieldset>

			<fieldset>
				<legend>Route</legend>
				<select value={routeKey} onChange={(e) => changeRoute(e.target.value)}>
					{ROUTES.map(({ key, method, path }) => (
						<option key={key} value={key}>
							{method} /pes{path}
						</option>
					))}
				</select>
				{route.note && <div style={{ color: 'gray' }}>{route.note}</div>}
				<div key={`${routeKey}-${resetCount}`}>
					{routeFields(route).map(renderField)}
					{route.surcharges && (
						<details>
							<summary>Surcharges d'en-tête</summary>
							{SURCHARGE_FIELDS.map(renderField)}
						</details>
					)}
				</div>
				<button type="button" disabled={loading} onClick={send}>
					{loading ? 'Envoi…' : 'Envoyer'}
				</button>{' '}
				<button type="button" onClick={clearAll}>
					Effacer tout
				</button>
			</fieldset>

			{error && <pre style={{ color: 'red' }}>{error}</pre>}

			{sent && (
				<fieldset>
					<legend>Requête envoyée</legend>
					<pre style={{ whiteSpace: 'pre-wrap' }}>{sent}</pre>
				</fieldset>
			)}

			{result && (
				<fieldset>
					<legend>Réponse</legend>
					<div>
						Statut : <b>{result.status}</b> —{' '}
						{result.contentType || '(sans Content-Type)'}
					</div>
					{Object.entries(result.pesHeaders).map(([name, value]) => (
						<div key={name}>
							{name} : <b>{value}</b>
						</div>
					))}
					{result.fichier && <div>Fichier téléchargé : {result.fichier}</div>}
					{asyncId && (
						<div>
							<button
								type="button"
								disabled={suivi !== ''}
								onClick={() => suivre(asyncId)}
							>
								Suivre le traitement {asyncId}
							</button>
						</div>
					)}
					{validationErrors && (
						<ul style={{ color: 'red', fontWeight: 'bold' }}>
							{Object.entries(validationErrors).map(([name, messages]) => (
								<li key={name}>
									{name} :{' '}
									{Array.isArray(messages)
										? messages.join(' ')
										: String(messages)}
								</li>
							))}
						</ul>
					)}
					{result.json !== undefined && (
						<pre
							style={{
								whiteSpace: 'pre-wrap',
								background: result.status >= 400 ? '#fee' : '#f4f4f4',
								padding: 8,
							}}
						>
							{JSON.stringify(result.json, null, 2)}
						</pre>
					)}
					{result.text && (
						<pre style={{ whiteSpace: 'pre-wrap' }}>{result.text}</pre>
					)}
				</fieldset>
			)}

			{suivi && (
				<div>
					Suivi du traitement {suivi} toutes les 2 s…{' '}
					<button type="button" onClick={stopPolling}>
						Arrêter
					</button>
				</div>
			)}

			{history.length > 0 && (
				<fieldset>
					<legend>Historique</legend>
					<table>
						<tbody>
							{history.map((h) => (
								<tr key={h.id}>
									<td>{h.at}</td>
									<td>{h.route}</td>
									<td>{h.status}</td>
									<td>{h.duree} ms</td>
								</tr>
							))}
						</tbody>
					</table>
				</fieldset>
			)}
		</div>
	);
};

export default TestPes;
